// client/src/services/contactsService.js
// Contacts cache service with 7-day TTL and SWR functionality

import api from '../utils/api';

// Cache configuration
const CACHE_KEY_PREFIX = 'velox:contacts';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SCHEMA_VERSION = 1;

// Generate cache key for user
const getCacheKey = (userId) => `${CACHE_KEY_PREFIX}:${userId}:v${SCHEMA_VERSION}`;

// Background revalidation debounce (5 seconds)
let revalidationTimeouts = new Map();

/**
 * Get contacts from cache if valid (within TTL)
 * @param {string} userId - User ID
 * @returns {Object|null} Cached data or null if expired/missing
 */
export function getContactsCached(userId) {
  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    
    if (!raw) return null;
    
    const cache = JSON.parse(raw);
    
    // Validate cache structure
    if (!cache?.data || !cache?.lastUpdated || cache?.schemaVersion !== SCHEMA_VERSION) {
      return null;
    }
    
    // Check if cache is expired
    if (Date.now() - cache.lastUpdated > TTL_MS) {
      return null;
    }
    
    return cache;
  } catch (error) {
    console.warn('Failed to read contacts cache:', error);
    return null;
  }
}

/**
 * Set contacts in cache
 * @param {string} userId - User ID
 * @param {Array} data - Contacts data
 * @param {string} etag - Optional ETag from server
 */
export function setContactsCache(userId, data, etag = null) {
  try {
    const cacheKey = getCacheKey(userId);
    const cacheData = {
      data,
      lastUpdated: Date.now(),
      ttlMs: TTL_MS,
      schemaVersion: SCHEMA_VERSION,
      ...(etag ? { etag } : {})
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // Gracefully handle quota exceeded or other storage errors
    console.warn('Failed to save contacts cache:', error);
  }
}

/**
 * Merge a new contact into the cached contacts list
 * @param {string} userId - User ID
 * @param {Object} contact - New contact to merge
 */
export function mergeContactIntoCache(userId, contact) {
  try {
    const cached = getContactsCached(userId);
    if (!cached) return;
    
    const contacts = [...cached.data];
    const existingIndex = contacts.findIndex(c => c._id === contact._id);
    
    if (existingIndex >= 0) {
      // Update existing contact
      contacts[existingIndex] = { ...contacts[existingIndex], ...contact };
    } else {
      // Add new contact at the beginning (most recent)
      contacts.unshift(contact);
    }
    
    // Update cache with merged data
    setContactsCache(userId, contacts, cached.etag);
  } catch (error) {
    console.warn('Failed to merge contact into cache:', error);
  }
}

/**
 * Clear contacts cache for user
 * @param {string} userId - User ID
 */
export function clearContactsCache(userId) {
  try {
    const cacheKey = getCacheKey(userId);
    localStorage.removeItem(cacheKey);
    
    // Clear any pending revalidation
    if (revalidationTimeouts.has(userId)) {
      clearTimeout(revalidationTimeouts.get(userId));
      revalidationTimeouts.delete(userId);
    }
  } catch (error) {
    console.warn('Failed to clear contacts cache:', error);
  }
}

/**
 * Fetch contacts from API with optional ETag support
 * @param {string} etag - Optional ETag for conditional request
 * @returns {Promise<{data: Array, etag?: string}>}
 */
export async function fetchContactsFromApi(etag = null) {
  try {
    const headers = {};
    if (etag) {
      headers['If-None-Match'] = etag;
    }
    
    const response = await api.get('/api/contact', { headers });
    
    return {
      data: response.data,
      etag: response.headers.etag || null
    };
  } catch (error) {
    // Handle 304 Not Modified
    if (error.response?.status === 304) {
      return { data: null, etag };
    }
    throw error;
  }
}

/**
 * Background revalidation with debouncing
 * @param {string} userId - User ID
 * @param {Function} onUpdate - Callback when data is updated
 */
export function backgroundRevalidate(userId, onUpdate) {
  // Clear existing timeout
  if (revalidationTimeouts.has(userId)) {
    clearTimeout(revalidationTimeouts.get(userId));
  }
  
  // Set new timeout for debounced revalidation
  const timeout = setTimeout(async () => {
    try {
      const cached = getContactsCached(userId);
      const { data, etag } = await fetchContactsFromApi(cached?.etag);
      
      if (data) {
        // Update cache with new data
        setContactsCache(userId, data, etag);
        
        // Notify callback if data changed
        if (!cached || JSON.stringify(cached.data) !== JSON.stringify(data)) {
          onUpdate(data);
        }
      }
    } catch (error) {
      console.warn('Background revalidation failed:', error);
    } finally {
      revalidationTimeouts.delete(userId);
    }
  }, 5000); // 5 second debounce
  
  revalidationTimeouts.set(userId, timeout);
}

/**
 * Prefetch contacts for user (non-blocking)
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function prefetchContacts(userId) {
  try {
    // Only prefetch if no valid cache exists
    const cached = getContactsCached(userId);
    if (cached) return;
    
    const { data, etag } = await fetchContactsFromApi();
    if (data) {
      setContactsCache(userId, data, etag);
    }
  } catch (error) {
    console.warn('Failed to prefetch contacts:', error);
  }
}
